import type { RentalManagerAuthCheckUserInfoDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { RentalManagerEnvironment } from '@zg-rentals/rental-manager-env-base';
import { BootstrapBrowserBuilder } from '@zg-rentals/rp-bootstrap-browser';
import { RentalManagerBrowserEnvironment } from '@zg-rentals/rental-manager-env-base';
import { WebSocketWrapper } from '@zg-rentals/particles-js-websocket-wrapper';
import { createBrowserLoggerBindings } from './loggerUtils';
import { makeDefaultEnvironment } from '../components';
import { type Logger, setGlobalLogger } from '@zg-rentals/logger-base';
import { urlUtils } from './urlUtils';

declare global {
  interface Window {
    __RENTAL_MANAGER_ENVIRONMENT__: RentalManagerEnvironment;
    log: Logger;
    __USER_INFO__?: RentalManagerAuthCheckUserInfoDto;
    __EXPERIMENTS__?: Record<string, string>;
  }
}

export type InitializeBrowserOptions = {
  appName: string;
  ddApplicationId?: string;
  ddClientToken?: string;
  onBootstrapRegister?: () => void;
};

const setupRentalManagerApiWebSocket = (webSocketHost: string) => {
  const wsUrl = `wss://${webSocketHost}rental-manager-api/ws/services`;
  window.rentalManagerApiWebSocket = new WebSocketWrapper({
    url: wsUrl,
    // wait up to 5 minutes before trying to reconnect again
    // resets on successfully conn
    // wait time doubles every failed conn attempt
    maxReconnect: 60000 * 5,
  });
};

// use this when no node back end
export const initializeNodelessBrowser = ({
  zgApi,
  appName,
  isProduction,
  buildNumber,
  ddApplicationId,
  ddClientToken,
  onBootstrapRegister,
}: {
  zgApi: string;
  isProduction: boolean;
  buildNumber: number;
} & InitializeBrowserOptions) => {
  const defaultEnv = makeDefaultEnvironment();
  defaultEnv.nodeEnv.zgApi = zgApi;
  window.__RENTAL_MANAGER_ENVIRONMENT__ = defaultEnv;
  const instance: RentalManagerEnvironment = RentalManagerBrowserEnvironment.setInstance(defaultEnv);
  instance.isNodeLess = true;
  instance.nodeEnv.isProduction = isProduction;
  instance.nodeEnv.buildId = String(buildNumber);
  instance.isClient = true;
  instance.isServer = false;

  if (window.__USER_INFO__) {
    instance.user = window.__USER_INFO__;
    instance.initialAuthComplete = true;
  }
  if (window.__EXPERIMENTS__) {
    instance.experiments = window.__EXPERIMENTS__;
  }
  return initializeBrowser({
    appName,
    browserOnlyInstance: defaultEnv,
    ddApplicationId,
    ddClientToken,
    onBootstrapRegister,
  });
};

// Use this when picking up variables from back end
export const initializeBrowser = ({
  browserOnlyInstance,
  ddApplicationId,
  ddClientToken,
  appName,
  onBootstrapRegister,
}: {
  browserOnlyInstance?: RentalManagerEnvironment;
} & InitializeBrowserOptions): RentalManagerEnvironment => {
  const instance: RentalManagerEnvironment = RentalManagerBrowserEnvironment.setInstance(
    browserOnlyInstance ? browserOnlyInstance : window.__RENTAL_MANAGER_ENVIRONMENT__,
  );

  instance.isServer = false;
  instance.isClient = true;

  const builder = new BootstrapBrowserBuilder(appName, Number(instance.nodeEnv.buildId))
    .withLoggerOptions({
      logPath: instance.nodeEnv.isProduction ? `/rental-manager-api/hostedApp/log?appName=${appName}` : undefined,
      level: instance.nodeEnv.isProduction ? 'error' : 'info',
      browser: {
        serialize: true,
      },
    })
    .withOnRegister(() => onBootstrapRegister?.());

  if (instance.nodeEnv.isProduction && ddApplicationId && ddClientToken) {
    builder.withDatadogPlugin(ddApplicationId, ddClientToken);
  }

  const bootstrap = builder.build();

  // Note: maybe todo, await?
  bootstrap.register();

  instance.monitor = bootstrap.monitor;
  instance.logger = bootstrap.logger.child(createBrowserLoggerBindings());

  setGlobalLogger(instance.logger);
  window.log = instance.logger;

  const hostParts = window.location.host.split(':');
  instance.nodeEnv.protocol = window.location.protocol.replace(':', '');
  instance.nodeEnv.hostname = hostParts[0];
  instance.nodeEnv.remotePort = hostParts[1]
    ? hostParts[1]
    : window.location.protocol.includes('https')
      ? '8443'
      : '80';

  const webSocketHost = urlUtils.removeProtocol(instance.nodeEnv.zgApi);
  setupRentalManagerApiWebSocket(webSocketHost);

  return instance;
};

export const getFrontEndInstance = (): RentalManagerEnvironment => {
  return RentalManagerBrowserEnvironment.getInstance();
};
